import {
  type QueryFunctionContext,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  APP_VERSION_POLL_INTERVAL_MS,
  APP_VERSION_QUERY_KEY,
  APP_VERSION_URL,
} from "@/constants/app-update";
import { parseDeployedAppBuildId } from "@/lib/app-update/parse-deployed-app-build-id";

// NOTE: NSAI-8170 — a static file next to index.html, not an /api route, so
// plain fetch rather than the API client. `no-store` keeps the browser (and,
// through the request's no-cache headers, any proxy) from answering with an
// earlier copy.
//
// An answer that names no build — a 5xx, or an old replica's index.html in
// the middle of a rolling upgrade — is a failed check, not news that nothing
// newer is deployed. It throws, so React Query keeps the last id it read and
// a toast already on screen stays there. `signal` lets React Query cancel a
// request that outlives its component.
const getDeployedAppBuildId = async ({
  signal,
}: QueryFunctionContext): Promise<string> => {
  const response = await fetch(APP_VERSION_URL, { cache: "no-store", signal });
  const buildId = response.ok
    ? parseDeployedAppBuildId({ body: await response.text() })
    : null;

  if (!buildId) {
    throw new Error(`Unreadable ${APP_VERSION_URL} (${response.status})`);
  }

  return buildId;
};

// NOTE: NSAI-8170 — the id of the build the server deploys right now. Asked on
// mount, every APP_VERSION_POLL_INTERVAL_MS while the tab is visible, and again
// whenever the tab comes back into view or back online (staleTime is 0). The
// return to the tab is the moment that matters most — a tab left in the
// background over a deploy — so this overrides the app-wide
// `refetchOnWindowFocus: false` (lib/query-client.ts). A failed check keeps
// the last answer and waits for the next tick instead of retrying.
export const useDeployedAppBuildId = (): UseQueryResult<string> =>
  useQuery({
    queryKey: [APP_VERSION_QUERY_KEY],
    queryFn: getDeployedAppBuildId,
    refetchInterval: APP_VERSION_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
